import type { ChangeEvent, KeyboardEvent } from "react";
import { useRef, useState } from "react";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";

import { StyledInput, StyledFieldContainer, StyledIconContainer } from "./styles";

type InteractiveTextInputProps = {
    inputId?: string;
    iconName?: IconName;
    className?: string;
    placeholder?: string;
    maxLength?: number;
    dataAutomation?: string;
    isHighlightedOnFocus?: boolean;
    onAdd(value: string): void;
    transformValue?(value: string): string;
};

const InteractiveTextInput = (props: InteractiveTextInputProps) => {
    const {
        onAdd,
        placeholder,
        dataAutomation,
        maxLength,
        inputId,
        className = null,
        iconName,
        isHighlightedOnFocus = false,
        transformValue = (value) => value,
    } = props;
    const inputRef = useRef(null);
    const [value, setValue] = useState("");

    function handleKeyUp(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === "Enter" && isValueValid()) {
            onAdd(value.trim());
            setValue("");
        }

        if (e.key === "Escape") {
            setValue("");
            inputRef?.current?.blur();
        }
    }

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
        const { value } = e.target;
        setValue(transformValue(value));
    }

    function isValueValid() {
        return value.trim().length > 0;
    }

    return (
        <StyledFieldContainer
            className={className}
            hasIcon={typeof iconName !== "undefined"}
            isHighlightedOnFocus={isHighlightedOnFocus}
        >
            {iconName ? <StyledIconContainer>
                    <SWReactIcons
                        iconName={iconName}
                        size="sm"
                    />
                </StyledIconContainer> : null}
            <StyledInput
                type="text"
                id={inputId}
                value={value}
                ref={inputRef}
                maxLength={maxLength ?? 20}
                onKeyUp={handleKeyUp}
                onChange={handleChange}
                placeholder={placeholder}
                data-automation={dataAutomation}
            />
        </StyledFieldContainer>
    );
}

export default InteractiveTextInput;
